import React, {
  useState,
  useContext,
  useRef,
  useCallback,
  useMemo,
  useEffect,
} from 'react';
import { connect } from 'react-redux';

import InfoIcon from 'mdi-react/InfoOutlineIcon';

import { AppContext } from '@prisjakt/ui-app-context';
import T from '@prisjakt/ui-translate';
import Panel from '@prisjakt/ui-panel';
import Pagination from '@prisjakt/ui-pagination';
import Button from '@prisjakt/ui-button';
import UserReviewRow from '@prisjakt/ui-user-review-row';

import UserAvatar from '../../components/user-avatar';
import AdContainer from '../../components/ads';
import ReportReviewModal from './components/report';
import UserReviewPrompt from './components/dialog-prompt';
import UserReviewDialog from './components/dialog';
import InfoPageLink from '../../components/info-page-link';
import LoginPromoModal from '../../components/modal/login-promo';

import { ViewportContext } from '../../components/viewport';
import {
  productReviewsListed,
  storeReviewsListed,
  productReviewViewed,
  storeReviewViewed,
} from './tracking';

import * as actions from './actions';

import { AppState } from '../../reducers';
import {
  UserReview,
  UserReviewSummary,
  ProductUserReviews,
  StoreUserReviews,
  UserReviewTranslation,
} from '../../types';

import * as S from './styles';

function groupByDate(nodes: UserReview[]) {
  const newReviewLimit = new Date();
  newReviewLimit.setFullYear(newReviewLimit.getFullYear() - 1);

  return nodes.reduce(
    (groups, review) => {
      const groupId =
        review.updatedAt &&
        review.createdAt &&
        new Date(review.updatedAt || review.createdAt) < newReviewLimit
          ? 'oldReviews'
          : 'reviews';
      return { ...groups, [groupId]: [...groups[groupId], review] };
    },
    { oldReviews: [], reviews: [] }
  );
}

type Translation = UserReviewTranslation & {
  text: UserReviewTranslation['reviewText'];
};

type OwnProps = {
  nodes: UserReview[];
  myReview: UserReview;
  pageInfo: ProductUserReviews['pageInfo'] | StoreUserReviews['pageInfo'];
  summary: UserReviewSummary;
  belongsTo: {
    id: string | number;
    name: string;
    type: 'product' | 'store';
  };
  groupByOldAndNew?: boolean;
};

type StateProps = {
  user: {
    id: string;
    displayName: string;
  } | null;
  isAuthenticated?: boolean;
  likedReviews?: string[];
};

type DispatchProps = {
  likeReviewToggle: typeof actions.likeReviewToggle;
};

type Props = OwnProps & StateProps & DispatchProps;

const UserReviews = ({
  user,
  nodes,
  myReview,
  pageInfo,
  belongsTo,
  groupByOldAndNew,
  summary,
  isAuthenticated,
  likedReviews,
  likeReviewToggle,
}: Props) => {
  const [reportReview, setReportReview] = useState<UserReview | null>(null);
  const [showLoginPromo, setShowLoginPromo] = useState(false);
  const containerElement = useRef<HTMLDivElement>(null);

  const { t, market } = useContext(AppContext);
  const { isDesktop } = useContext(ViewportContext);

  useEffect(() => {
    if (belongsTo.type === 'product') {
      productReviewsListed(pageInfo.sort?.name, { offset: pageInfo.offset });
    }
    if (belongsTo.type === 'store') {
      storeReviewsListed(pageInfo.sort?.name, { offset: pageInfo.offset });
    }
  }, [pageInfo.offset, pageInfo.sort, belongsTo.type]);

  const scrollToTop = useCallback(() => {
    if (
      containerElement.current &&
      'scrollIntoView' in containerElement.current
    ) {
      containerElement.current.scrollIntoView({
        block: 'start',
        behavior: 'smooth',
      });
    }
  }, []);

  const onCompleteReport = useCallback(() => {
    setReportReview(null);
  }, []);

  const isPurchaseDateModuleActive = useCallback(() => {
    return (
      market.modules?.userReviewPurchaseDate?.enabled &&
      belongsTo.type === 'product'
    );
  }, [belongsTo.type, market.modules]);

  const userReviewRowActions = useMemo(
    () => [
      {
        name: t('feedback', 'report-review.report-button'),
        handler: (reportedReview: UserReview) =>
          setReportReview(reportedReview),
      },
    ],
    [t]
  );

  const handleVote = useCallback(
    (id, reviewType) => {
      if (isAuthenticated) {
        likeReviewToggle(id, reviewType);
      } else {
        setShowLoginPromo(true);
      }
    },
    [isAuthenticated]
  );

  const hideLoginPromo = useCallback(() => {
    setShowLoginPromo(false);
  }, []);

  const renderReview = (
    review: UserReview & {
      localizedCreatedAt?: UserReview['createdAt'];
      localizedUpdatedAt?: UserReview['updatedAt'];
    }
  ) => {
    if (!review.id) return null;

    const isMyReview = user && review.user && user.id === review.user.id;

    let trackViewed;
    if (belongsTo.type === 'product') {
      trackViewed = productReviewViewed;
    } else if (belongsTo.type === 'store') {
      trackViewed = storeReviewViewed;
    }

    return !review.underInvestigation ? (
      <UserReviewRow
        id={review.id}
        key={review.id}
        rating={review.rating}
        date={review.localizedCreatedAt}
        purchaseDate={
          isPurchaseDateModuleActive() && review.purchaseDate
            ? review.purchaseDate
            : undefined
        }
        editDate={
          review.localizedCreatedAt !== review.localizedUpdatedAt
            ? review.localizedUpdatedAt
            : ''
        }
        userId={review.user && review.user.id}
        displayName={review.user && review.user.displayName}
        userReviewCount={review.user && review.user.reviewCount}
        text={review.reviewText}
        translation={review.translation as Translation}
        editControl={
          isMyReview ? (
            <UserReviewPrompt
              control={
                <S.EditButton>{t('reviews', 'button.edit')}</S.EditButton>
              }
            />
          ) : null
        }
        avatar={
          <UserAvatar
            styling="dark"
            size={30}
            displayName={review.user && review.user.displayName}
            avatar={review.user && review.user.avatar}
          />
        }
        actions={userReviewRowActions}
        reply={
          review.reply && belongsTo.name
            ? {
                name: belongsTo.name,
                text: review.reply,
                date: review.replyDate,
              }
            : undefined
        }
        trackViewed={trackViewed}
        isMyReview={isMyReview || false}
        toggleVote={() => handleVote(review.id, belongsTo.type)}
        liked={likedReviews && likedReviews.includes(String(review.id))}
      />
    ) : null;
  };

  const { oldReviews, reviews } = groupByOldAndNew
    ? groupByDate(nodes)
    : { reviews: nodes, oldReviews: [] };

  return (
    <div ref={containerElement}>
      <LoginPromoModal
        isOpen={showLoginPromo}
        onRequestClose={hideLoginPromo}
      />

      <ReportReviewModal
        review={reportReview}
        onComplete={onCompleteReport}
        type={belongsTo.type}
      />

      {user && (
        <UserReviewDialog
          belongsToId={belongsTo.id}
          reviewType={belongsTo.type.toUpperCase()}
          myReview={myReview}
          setDisplayName={!user.displayName}
          title={t('reviews', 'dialog.title', { name: belongsTo.name })}
          setPurchaseDate={isPurchaseDateModuleActive()}
        />
      )}
      <Panel
        footer={
          <Pagination
            pageInfo={pageInfo}
            onNext={scrollToTop}
            onPrev={scrollToTop}
            onReset={scrollToTop}
          />
        }
      >
        <S.Summary>
          <>
            {summary.rating &&
              summary.countTotal &&
              summary.distribution &&
              summary.distribution.length > 0 && (
                <S.ReviewSummary
                  rating={summary.rating}
                  count={summary.countTotal}
                  distribution={summary.distribution}
                />
              )}

            <S.SummaryButtons
              hasReviews={
                typeof summary.countTotal === 'number' && summary.countTotal > 0
              }
            >
              <S.Prompt highlighted={nodes.length === 0}>
                <UserReviewPrompt
                  firstToReview={nodes.length === 0 && !summary.rating}
                  firstToReviewText={t('reviews', 'info.first-review', {
                    name: belongsTo.name,
                  })}
                  requireLogin={!user}
                  control={
                    <Button
                      color="primary"
                      data-test="WriteReviewButton"
                      variant={
                        isDesktop || summary.countTotal === 0
                          ? 'default'
                          : 'text'
                      }
                      style={
                        !isDesktop && summary.countTotal !== 0
                          ? { padding: 0 }
                          : {
                              width: summary.countTotal === 0 ? '60%' : 'auto',
                            }
                      }
                    >
                      {t('reviews', 'button.write-review')}
                    </Button>
                  }
                />
              </S.Prompt>

              <S.InfoLink hasReviews={nodes.length > 0}>
                <InfoPageLink id={market.infoPageMap?.aboutReviews}>
                  <InfoIcon />{' '}
                  {isDesktop && t('reviews', 'reviews.about-reviews')}
                </InfoPageLink>
              </S.InfoLink>
            </S.SummaryButtons>
          </>
        </S.Summary>

        <AdContainer
          placement="panorama_extra"
          format="panorama_1_wde"
          min={861}
          max={10000}
          scalable
          scaleBase=".pj-ui-panel--row"
          spacing={{ top: '10px', bottom: '10px' }}
        />

        <AdContainer
          placement="dubbelpanorama1"
          format="panorama_1"
          min={0}
          max={860}
          spacing={{ top: '10px', bottom: '10px' }}
        />
        <div style={{ display: 'flex', flexDirection: 'row' }}>
          {nodes.length > 0 && (
            <S.SortedBy style={{ marginLeft: 'auto' }}>
              <T domain="reviews">info.sort-by-date</T>
            </S.SortedBy>
          )}
        </div>

        {reviews.map(renderReview)}

        {oldReviews.length > 0 && (
          <S.OldReviewsHeader>
            <S.SortedBy>
              <T domain="reviews">info.sort-by-date</T>
            </S.SortedBy>
            <h5>
              <T domain="reviews">panel.old-title</T>
            </h5>
            <p>
              <T domain="reviews">panel.old-lead</T>
            </p>

            {oldReviews.map(renderReview)}
          </S.OldReviewsHeader>
        )}
      </Panel>
    </div>
  );
};

const mapStateToProps = (state: AppState) => {
  if (!state.areas.account.me) {
    return {
      user: null,
      isAuthenticated: false,
    };
  }

  return {
    user: {
      id: state.areas.account.me.id,
      displayName: state.areas.account.me.displayName,
    },
    isAuthenticated: state.areas.account.isAuthenticated,
    likedReviews: state.areas.account.me.likes.reviews,
  };
};

const dispatchActions = {
  likeReviewToggle: actions.likeReviewToggle,
};

export default connect(mapStateToProps, dispatchActions)(UserReviews);
